"use client";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { TotalSalesByYearAndMonthData } from "@/modules/stochastic/features/DashboardPage/api/fetchStochasticDashboardData/types";
import {
  formatCurrency,
  generateColors,
} from "@/modules/stochastic/features/DashboardPage/utils/chart";
import ChartTooltipWithColoredYears from "@/modules/stochastic/features/DashboardPage/components/ChartTooltipWithColoredYears/ChartTooltipWithColoredYears";
import { ChartBarLegendWithColoredYear } from "@/modules/stochastic/features/DashboardPage/components/ChartBarLegendWithColoredYear/ChartBarLegendWithColoredYear";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

type TotalSalesByYearAndMonthChartProps = {
  chartId?: string;
  initialData: TotalSalesByYearAndMonthData;
};

export default function TotalSalesByYearAndMonthChart({
  initialData,
}: TotalSalesByYearAndMonthChartProps) {
  if (!initialData?.items || initialData.items.length === 0) return null;

  const { theme } = useTheme();
  const { years, items } = initialData;
  const colors = generateColors(years, theme);

  return (
    <Card className="w-full bg-white dark:bg-secondary-dark">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Total Sales by Year & Month
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Provides context for changes in market capture over time and
                  supports consistent, year-round marketing execution.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Monthly sales trends showing growth patterns and seasonality
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer height={500} width="100%">
          <BarChart
            data={items}
            margin={{ top: 20, right: 30, left: 45, bottom: 5 }}
          >
            <CartesianGrid
              stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="month"
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
            />
            <YAxis
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              tickFormatter={formatCurrency}
            />
            <ChartTooltip
              content={
                <ChartTooltipWithColoredYears
                  colors={colors}
                  labelKey="month"
                  labelPrefix="Month:"
                  valueSuffix=""
                />
              }
            />
            <Legend content={<ChartBarLegendWithColoredYear />} />
            {years.map((year) => (
              <Bar dataKey={year} fill={colors[year]} key={year} name={year} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}